import React from 'react';
import { StylesProvider } from '@material-ui/core/styles';
import { Navigate, Route } from 'react-router';
import {
  createUnifiedTheme,
  UnifiedThemeProvider,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CatalogContent } from './components/catalog/CustomCatalogPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { FlatRoutes } from '@backstage/core-app-api';
import { createApp } from '@backstage/app-defaults';
import { orgPlugin } from '@backstage/plugin-org';
import {
  googleAuthApiRef,
  discoveryApiRef,
  useApi,
  configApiRef,
  useAnalytics,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { GetArtifactPage, BuildkitePage } from 'plugin-buildkite';

// Parses supplied JWT token and returns the payload
function parseJwt(token: string): { exp: number } {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => {
        const charHex = c.charCodeAt(0).toString(16);
        const charHexPadded = `00${charHex}`.slice(-2);
        return `%${charHexPadded}`;
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

// Returns milliseconds until the supplied JWT token expires
function msUntilExpiry(token: string): number {
  const payload = parseJwt(token);
  const remaining =
    new Date(payload.exp * 1000).getTime() - new Date().getTime();
  return remaining;
}

// Calls the specified url regularly using an auth token to set a token cookie
// to authorize regular HTTP requests when loading techdocs
async function setTokenCookie(url: string, token: string) {
  const fetchResult = await fetch(url, {
    mode: 'cors',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!fetchResult.ok) {
    throw new Error(
      `Cookie fetch not OK: ${fetchResult.status} ${fetchResult.statusText}`,
    );
  }
  // Call this function again a few minutes before the token expires
  const ms = msUntilExpiry(token) - 4 * 60 * 1000;
  setTimeout(
    () => {
      setTokenCookie(url, token);
    },
    ms > 0 ? ms : 10000,
  );
}

const identityProvider: SignInProviderConfig = {
  id: 'google-auth-provider',
  title: 'Google',
  message: 'Sign In using Google',
  apiRef: googleAuthApiRef,
};

const FONT_FAMILY = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

// Butterfly brand blues — matches butterflynetwork.com
const BFLY_BLUE = '#0066CC';
const BFLY_BLUE_DARK = '#0047A0';
const BFLY_TEAL = '#00CFC5';

const bflyPageTheme = {
  home: genPageTheme({ colors: [BFLY_BLUE, BFLY_BLUE_DARK], shape: shapes.wave }),
  tool: genPageTheme({ colors: [BFLY_BLUE_DARK, '#001F6B'], shape: shapes.round }),
  service: genPageTheme({ colors: [BFLY_BLUE, BFLY_BLUE_DARK], shape: shapes.wave }),
  website: genPageTheme({ colors: [BFLY_BLUE_DARK, '#001F6B'], shape: shapes.wave }),
  library: genPageTheme({ colors: [BFLY_BLUE, BFLY_TEAL], shape: shapes.wave2 }),
  documentation: genPageTheme({ colors: [BFLY_BLUE, BFLY_TEAL], shape: shapes.wave2 }),
  other: genPageTheme({ colors: [BFLY_BLUE_DARK, '#001F6B'], shape: shapes.wave }),
  app: genPageTheme({ colors: [BFLY_BLUE, BFLY_BLUE_DARK], shape: shapes.wave }),
  apis: genPageTheme({ colors: [BFLY_TEAL, BFLY_BLUE], shape: shapes.wave2 }),
  card: genPageTheme({ colors: [BFLY_BLUE, BFLY_TEAL], shape: shapes.wave }),
};

const componentOverrides = {
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { borderRadius: 12 },
      elevation1: {
        boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: 8, textTransform: 'none' as const, fontWeight: 600 },
      containedPrimary: {
        boxShadow: '0 2px 8px rgba(0,102,204,0.3)',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,102,204,0.4)' },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 10, fontWeight: 500 },
      standardError: { border: '1px solid #fca5a5' },
      standardWarning: { border: '1px solid #fcd34d' },
      standardInfo: { border: '1px solid #93c5fd' },
      standardSuccess: { border: '1px solid #86efac' },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
      },
      outlined: {
        backgroundColor: '#EEF4FF',
        borderColor: '#BFDBFE',
        color: '#0066CC',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: '1px solid #F3F4F6', fontSize: '0.875rem' },
      head: {
        fontWeight: 700,
        fontSize: '0.72rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: '#6B7280',
        backgroundColor: '#F9FAFB',
        borderBottom: '2px solid #E5E7EB',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: '#F5F7FF' } },
    },
  },
};

const customLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: BFLY_BLUE },
    secondary: { main: BFLY_TEAL },
    navigation: {
      ...palettes.light.navigation,
      background: '#0A1628',
      indicator: BFLY_TEAL,
      color: '#8FA3BF',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#162340' },
      submenu: { background: '#162340' },
    },
    background: { default: '#F0F2F5', paper: '#FFFFFF' },
    link: BFLY_BLUE,
    linkHover: BFLY_BLUE_DARK,
  },
  fontFamily: FONT_FAMILY,
  pageTheme: bflyPageTheme,
  defaultPageTheme: 'home',
  components: componentOverrides,
});

const customDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#4DB8FF' },
    secondary: { main: BFLY_TEAL },
    navigation: {
      ...palettes.dark.navigation,
      background: '#060D1A',
      indicator: BFLY_TEAL,
      color: '#7A95B5',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#0D1F38' },
      submenu: { background: '#0D1F38' },
    },
    background: { default: '#0D1117', paper: '#161B22' },
    link: '#4DB8FF',
    linkHover: '#80CFFF',
  },
  fontFamily: FONT_FAMILY,
  pageTheme: bflyPageTheme,
  defaultPageTheme: 'home',
  components: {
    ...componentOverrides,
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid #21262D', fontSize: '0.875rem' },
        head: {
          fontWeight: 700,
          fontSize: '0.72rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
          color: '#9CA3AF',
          backgroundColor: '#1C2128',
          borderBottom: '2px solid #30363D',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: '#1C2438' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600, fontSize: '0.75rem', height: 24 },
        outlined: { backgroundColor: 'transparent', borderWidth: '1.5px' },
      },
    },
  },
});

const app = createApp({
  apis,
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customLightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customDarkTheme} children={children} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      const googleAuthApi = useApi(googleAuthApiRef);

      const configApi = useApi(configApiRef);
      const analyticsApi = useAnalytics();

      const onSignInSuccess = async (
        identityApi: IdentityApi,
      ): Promise<void> => {
        // When logged in, set a token cookie
        const token = (await identityApi.getCredentials()).token;
        if (typeof token !== 'undefined') {
          try {
            await setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              token,
            );
            const profileInfo = await identityApi.getProfileInfo();
            analyticsApi.captureEvent('login', profileInfo.email || 'noemail');
          } catch (error) {
            // If the token is invalid (backend service restarted?), we will need to get a new JWT by
            // re-authenticating by forcing a sign out.
            // The timeout ensures that props.onResult is called so that app is in loaded state, which is
            // required for signOut() to function correctly.
            setTimeout(() => googleAuthApi.signOut(), 1);
          }
        }
        // Forward results
        props.onSignInSuccess(identityApi);
      };
      if (configApi.get('app.title') === 'Butterfly Console E2E Test') {
        return (
          <SignInPage
            {...props}
            providers={['guest']}
            title="Sign in"
            align="center"
            onSignInSuccess={onSignInSuccess}
          />
        );
      }
      return (
        <SignInPage
          {...props}
          provider={identityProvider}
          title="Sign in"
          align="center"
          onSignInSuccess={onSignInSuccess}
        />
      );
    },
  },
  bindRoutes({ bind }: { bind: any }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const AppProvider: React.ComponentType<React.PropsWithChildren<{}>> = app.getProvider();
const AppRouter: React.ComponentType<React.PropsWithChildren<{}>> = app.getRouter();

const routes = (
  <FlatRoutes>
    <Navigate key="/" to="catalog" />
    <Route path="/catalog" element={<CatalogIndexPage {...{ filters: null, content: <CatalogContent /> } as any} />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/catalog-import" element={<CatalogImportPage />} />
    <Route path="/search" element={<SearchPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/buildkite-artifacts" element={<GetArtifactPage />} />
    <Route path="/buildkite" element={<BuildkitePage />} />

  </FlatRoutes>
);

const App = () => (
  <StylesProvider injectFirst>
    <AppProvider>
      <AlertDisplay />
      <OAuthRequestDialog />
      <AppRouter>
        <Root>{routes}</Root>
      </AppRouter>
    </AppProvider>
  </StylesProvider>
);

export default App;
